import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import basePCUrl from '../../../Globals';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Input from '@material-ui/core/Input';
import TextField from '@material-ui/core/TextField';
import { Chart } from 'react-google-charts';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TrainingTotalsWidget from './TrainingTotalsWidget';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import './TrainingSuite.css';
import { fetchApiData } from '../../../utils/service';
import { generateTrainingReportData, generateChartData } from './trainingUtils';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const viewOption = ['Pie Chart', 'Donut'];
const statusType = ['Completed', 'InProgress', 'All'];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
}));
function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
}

const chartOptions = {
  title: 'Training Report',
  is3D: true,
};


export default function TrainingReport(props) {

  const preferredLanguage = props.preferredLanguage
  const getTranslation = props.getTranslation

  const classes = useStyles();
  const theme = useTheme();
  const { getAccessTokenSilently } = usePortalAuth();
  const [tableData, setTableData] = useState();
  const [personName, setPersonName] = React.useState('All');
  const [viewType, setViewType] = React.useState('Pie Chart');
  const [selectedUser, setSelectedUser] = React.useState('All');
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [chartData, setChartData] = React.useState([
    ['Status', 'Count'],
    ['Course Assigned', 0],
    ['Completed', 0],
    ['In Progress', 0],
  ]);

  
  const { isLoading, data, isError, error } = useQuery('training-stats', async () => {
    const token = await getAccessTokenSilently();
    return fetchApiData(basePCUrl, token, 'getClientTrainingStats', props.accessLevel, props.selectedClient.clientId);
  });
  const usertrainingData = useQuery('individual-training-stats', async () => {
    const token = await getAccessTokenSilently();
    return fetchApiData(basePCUrl, token, 'getUserLevelTrainingStats',  props.accessLevel, props.selectedClient.clientId);
  });

  useEffect(() => {
    data && setChartData(generateChartData(data, 'All'));
  }, [data]);

  const trainingData = useMemo(() => {
    if (!usertrainingData?.data) return [];
    return generateTrainingReportData(usertrainingData.data) || [];
  }, [usertrainingData?.data]);

  const userList = useMemo(
    () => trainingData.map((user) => user.userName),
    [trainingData]
  );

  const filteredTableRows = useMemo(() => {
    const rows =
      selectedUser === 'All'
        ? trainingData
        : trainingData.filter((user) => user.userName === selectedUser);
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => String(row?.userName || '').toLowerCase().includes(q));
  }, [trainingData, selectedUser, tableSearch]);

  if (isLoading || usertrainingData.isLoading) {
    return <LoadingSpinner className="LinkTableSpinner" box="LinkTableSpinnerBox" />;
  }

  const handleChangeStatus = (event) => {
    const status = event.target.value;
    let data = [];
    if (status === 'InProgress') {
      data = trainingData.filter((data) => data?.trainingCoursesCompleted.length > 0);
    } else if (status === 'Completed') {
      data = trainingData.filter((data) => data?.trainingCoursesCompleted.length > 0);
    } else {
      data = trainingData;
    }
    setTableData(data);
    setPersonName(status);
  };
  const handleSelectUser = (event) => {
    const user = event.target.value;
    let individualChartData;
    const userData = trainingData.filter((data) => data.userName === user);
    if (viewType != 'Table') {
      individualChartData = user === 'All' ? generateChartData(data, 'All') : generateChartData(userData[0]);
      setChartData(individualChartData);
    }
    setSelectedUser(user);
  };
  const handleChangeView = (event) => {
    setViewType(event.target.value);
  };

  if (isError) {
    return <h2>{error.message}</h2>;
  }
  const getTableData = (rows = []) => {
    if (selectedUser == 'All') {
      return rows;
    }
    return rows.filter((user) => user.userName === selectedUser);
  };

  return (
    <div>
      <Grid container direction="row" justify="center" alignItems="flex-start" spacing={2} className={classes.root}>
        <Grid item xs={12}>
          <Paper className={classes.headerPaper}>
            <Typography variant="h5" component="h3" style={{ textAlign: 'center', padding: '5px' }}>
              Training Dashboard
            </Typography>
            <br />

            <Grid container direction="row">
              <Grid item align="center" xs={12} sm={4} md={4} lg={4}>
                <TrainingTotalsWidget
                  widgetNumber={data?.totalUsersAssignedTraining || 0}
                  widgetTitle={'Assigned Course'}
                />
              </Grid>
              <Grid item align="center" xs={12} sm={4} md={4} lg={4}>
                <TrainingTotalsWidget widgetNumber={data?.totalCoursesCompleted || 0} widgetTitle={'Completed'} />
              </Grid>
              <Grid item align="center" xs={12} sm={4} md={4} lg={4}>
                <TrainingTotalsWidget widgetNumber={data?.totalCoursesInProgress || 0} widgetTitle={'In Progress'} />
              </Grid>
            </Grid>

            <br />
          </Paper>
        </Grid>
      </Grid>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {viewType === 'Table' && trainingData && (
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-mutiple-name-label">Filter By Status</InputLabel>
            <Select
              labelId="demo-mutiple-name-label"
              id="demo-mutiple-name"
              value={personName}
              defaultValue="All"
              onChange={handleChangeStatus}
              input={<Input />}
              MenuProps={MenuProps}
            >
              {statusType.map((name) => (
                <MenuItem key={name} value={name} style={getStyles(name, personName, theme)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {viewType != 'Table' && userList.length > 0 && (
          <FormControl className={classes.formControl}>
            <InputLabel id="demo-mutiple-name-label">Select User</InputLabel>
            <Select
              labelId="demo-mutiple-name-label"
              id="demo-mutiple-name"
              value={selectedUser}
              onChange={handleSelectUser}
              input={<Input />}
              MenuProps={MenuProps}
            >
              {userList &&
                userList.map((name) => (
                  <MenuItem key={name} value={name} style={getStyles(name, personName, theme)}>
                    {name}
                  </MenuItem>
                ))}
              <MenuItem value="All">All Users</MenuItem>
            </Select>
          </FormControl>
        )}
        <FormControl className={classes.formControl}>
          <InputLabel id="demo-mutiple-name-label">Select View</InputLabel>
          <Select
            labelId="demo-mutiple-name-label"
            id="demo-mutiple-name"
            value={viewType}
            defaultValue="Pie Chart"
            onChange={handleChangeView}
            input={<Input />}
            MenuProps={MenuProps}
          >
            {viewOption.map((name) => (
              <MenuItem key={name} value={name} style={getStyles(name, personName, theme)}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <>
        <Chart
          chartType="PieChart"
          data={chartData}
          options={{
            ...chartOptions,
            ...(viewType === 'Donut' && { pieHole: 0.4, is3D: false }),
          }}
          width={'100%'}
          height={'400px'}
        />
        <Paper style={{ marginTop: 16, padding: 16 }}>
          <Typography variant="h6" style={{ marginBottom: 12 }}>
            Training Report View By Table
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            label="Search user"
            value={tableSearch}
            onChange={(e) => {
              setTableSearch(e.target.value);
              setTablePage(0);
            }}
            style={{ marginBottom: 12, minWidth: 240 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="center">Assigned Course</TableCell>
                  <TableCell align="center">Complete Course</TableCell>
                  <TableCell align="center">In Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTableRows
                  .slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.userName || row.userId || JSON.stringify(row)}>
                      <TableCell>{row.userName}</TableCell>
                      <TableCell align="center">{row?.trainingCoursesAssigned?.length || 0}</TableCell>
                      <TableCell align="center">{row?.trainingCoursesCompleted?.length || 0}</TableCell>
                      <TableCell align="center">{row?.trainingCoursesInProgress?.length || 0}</TableCell>
                    </TableRow>
                  ))}
                {filteredTableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No training rows
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredTableRows.length}
            page={tablePage}
            onPageChange={(_, next) => setTablePage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </>
    </div>
  );
}
